/* eslint-disable no-plusplus */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Paper, Typography } from "@material-ui/core";

import Loading from "../../../../components/CustomComponents/Loading";
import KPTextform from "../../../../components/KPTComponents/KPTextform";

import { formActions } from "../../../../actions";
import { isGtnsCouncil } from "../../../../utils/gtnsPermission";

// Điểm cho mỗi nội dung ưu tiên (tự động tính, giống QCV).
const PRIORITY_POINT = 5;

// Các nhóm thành tích KHCN (mục B) — điểm từng nhóm = tổng điểm các thành tích
// đã chấm trong nhóm đó (đồng bộ với Đăng ký hồ sơ / Xem hồ sơ).
const KHCN_TYPE_LIST = [
  { key: "formSolution", title: "1. Bằng độc quyền sáng chế, giải pháp hữu ích" },
  {
    key: "formScientificResearch",
    title: "2. Bài báo khoa học (tạp chí quốc tế, quốc gia)",
  },
  {
    key: "formScientificReport",
    title: "3. Bài báo khoa học (kỷ yếu/poster hội thảo)",
  },
  {
    key: "formReward",
    title: "4. Đề tài NCKH & Huy chương/Giải thưởng",
  },
];

export default function HoSoNote() {
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.formReducer.loading);
  const role = useSelector((state) => state.userReducer.user?.role);
  const applicationDetail = useSelector((state) => state.formReducer.focusForm);

  const [values, setValues] = useState({});

  const arrayPath = window.location.href.split("/");
  const urlId = parseInt(arrayPath.slice(arrayPath.length - 1));

  // ----- Điểm ưu tiên TỰ ĐỘNG (đọc từ field priority JSON, fallback dữ liệu cũ) -----
  const priorityInfo = useMemo(() => {
    let p = {};
    try {
      p = JSON.parse(applicationDetail?.priority || "{}") || {};
    } catch (e) {
      p = {};
    }
    const isEthnicMinority =
      p.isEthnicMinority ??
      (applicationDetail?.profile?.ethnic &&
        applicationDetail?.profile?.ethnic !== "Kinh");
    const isDisabled =
      p.isDisabled ??
      (applicationDetail?.bonus && applicationDetail?.bonus !== "khong");
    return { isEthnicMinority: !!isEthnicMinority, isDisabled: !!isDisabled };
  }, [applicationDetail]);

  const autoScoreF = priorityInfo.isEthnicMinority ? PRIORITY_POINT : 0;
  const autoScoreG = priorityInfo.isDisabled ? PRIORITY_POINT : 0;

  // ----- Điểm học tập (A) tự động theo GPA -----
  const autoScoreB = useMemo(() => {
    const gpa = applicationDetail?.gpa;
    if (gpa >= 3.6) return 10;
    if (gpa >= 3.2) return 7;
    return 0;
  }, [applicationDetail]);

  // ----- Điểm KHCN (B) tự động: tổng điểm các thành tích đã chấm -----
  const khcnByType = useMemo(() => {
    const sum = (arr) =>
      Math.round((arr || []).reduce((s, e) => s + (e?.score || 0), 0) * 100) /
      100;
    return KHCN_TYPE_LIST.map((t) => ({
      ...t,
      score: sum(applicationDetail?.[t.key]),
    }));
  }, [applicationDetail]);
  const autoScoreA =
    Math.round(khcnByType.reduce((s, t) => s + t.score, 0) * 100) / 100;

  // Nạp giá trị: ưu tiên dữ liệu đã chấm; nếu chưa có thì điền tự động (cho phép ghi đè).
  useEffect(() => {
    if (applicationDetail) {
      setValues((v) => ({
        ...v,
        note: applicationDetail?.note,
        scoreB: applicationDetail?.scoreB || autoScoreB,
        scoreD: applicationDetail?.scoreD,
        scoreE: applicationDetail?.scoreE,
        scoreF: applicationDetail?.scoreF || autoScoreF,
        scoreG: applicationDetail?.scoreG || autoScoreG,
      }));
    }
  }, [applicationDetail]);

  // Hội đồng BÌNH CHỌN: xem được toàn bộ điểm nhưng KHÔNG sửa, KHÔNG duyệt.
  const readOnly = isGtnsCouncil(role);

  // Chặn cả ở đây, không chỉ ẩn nút: KPTextform bọc trong <form onSubmit>, nếu
  // chỉ ẩn nút thì gõ Enter trong ô nhập vẫn submit được.
  const saveNote = () =>
    readOnly ||
    dispatch(
      formActions.updateBCD({
        ...values,
        scoreA: autoScoreA,
        id: urlId,
        status: "need_update",
      })
    );

  const handleSubmit = () =>
    readOnly ||
    dispatch(
      formActions.updateBCD({
        ...values,
        scoreA: autoScoreA,
        id: urlId,
        status: "approved",
      })
    );

  const sectionLabel = (text) => (
    <Typography variant="subtitle2" style={{ marginTop: 4 }}>
      <b>{text}</b>
    </Typography>
  );
  const noteLine = (text) => (
    <Typography variant="body2" style={{ color: "#555" }}>
      {text}
    </Typography>
  );

  const textForm = [
    // ===== A. Học tập =====
    {
      key: "a-label",
      width: 12,
      value: true,
      render: (
        <Box>
          {sectionLabel("A. Thành tích học tập (tự động theo điểm TB):")}
          {noteLine(
            `Điểm TB chung: ${applicationDetail?.gpa ?? "—"} → điểm tự chấm: ${autoScoreB} (≥ 3.6: 10đ; 3.2 – 3.6: 7đ)`
          )}
        </Box>
      ),
    },
    {
      key: "scoreB",
      width: 12,
      label: "Điểm chấm (giám khảo)",
      type: "float",
      value: values?.scoreB,
    },
    // ===== B. KHCN =====
    {
      key: "b-label",
      width: 12,
      value: true,
      render: (
        <Box style={{ marginTop: 8 }}>
          {sectionLabel("B. Thành tích khoa học công nghệ (tự động):")}
          {khcnByType.map((t) => (
            <Box
              key={t.key}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ padding: "3px 0", borderBottom: "1px dashed #eee" }}
            >
              <Typography variant="body2" style={{ paddingRight: 8 }}>
                {t.title}
              </Typography>
              <Typography variant="body2">
                <b>{t.score}</b>
              </Typography>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      key: "scoreA",
      width: 12,
      label: "Tổng điểm KHCN (tự động)",
      type: "float",
      disabled: true,
      value: autoScoreA,
    },
    // ===== C. Khen thưởng =====
    {
      key: "c-label",
      width: 12,
      value: true,
      render: (
        <Box style={{ marginTop: 8 }}>
          {sectionLabel("C. Khen thưởng thành tích KHCN:")}
        </Box>
      ),
    },
    {
      key: "scoreD",
      width: 12,
      label: "Điểm chấm (giám khảo)",
      type: "float",
      value: values?.scoreD,
    },
    // ===== D. Hoạt động Đoàn, Hội =====
    {
      key: "d-label",
      width: 12,
      value: true,
      render: (
        <Box style={{ marginTop: 8 }}>
          {sectionLabel("D. Thành tích hoạt động Đoàn, Hội:")}
        </Box>
      ),
    },
    {
      key: "scoreE",
      width: 12,
      label: "Điểm chấm (giám khảo)",
      type: "float",
      value: values?.scoreE,
    },
    // ===== E. Ưu tiên (tự động tính, cho phép ghi đè) =====
    {
      key: "e-label",
      width: 12,
      value: true,
      render: (
        <Box style={{ marginTop: 8 }}>
          {sectionLabel("E. Ưu tiên (tự động tính, cho phép ghi đè):")}
          {noteLine(
            `Điểm tự chấm: ${autoScoreF + autoScoreG} điểm (mỗi nội dung ưu tiên ${PRIORITY_POINT} điểm)`
          )}
          {noteLine(
            `+ Dân tộc thiểu số: ${priorityInfo.isEthnicMinority ? "Có" : "Không"}`
          )}
          {noteLine(
            `+ Sinh viên khuyết tật: ${priorityInfo.isDisabled ? "Có" : "Không"}`
          )}
        </Box>
      ),
    },
    {
      key: "scoreF",
      width: 12,
      label: "Điểm ưu tiên dân tộc thiểu số",
      type: "float",
      value: values?.scoreF,
    },
    {
      key: "scoreG",
      width: 12,
      label: "Điểm ưu tiên sinh viên khuyết tật",
      type: "float",
      value: values?.scoreG,
    },
    {
      key: "note",
      width: 12,
      height: 6,
      label: "Ghi chú của ban thư ký",
      value: values?.note,
    },
    {
      key: "button-2",
      width: 6,
      label: "Không duyệt hồ sơ",
      type: "button",
      onClick: saveNote,
    },
    { key: "submit", width: 6, label: "Duyệt hồ sơ", type: "submit" },
  ];

  // Chỉ đọc: bỏ 2 nút "Không duyệt hồ sơ"/"Duyệt hồ sơ" và khoá hết ô nhập.
  // Các dòng chỉ render (nhãn mục, bảng tách điểm) không bị ảnh hưởng vì
  // KPTextfield trả về props.render trước khi xét disabled.
  const formRows = readOnly
    ? textForm
        .filter((e) => e.type !== "button" && e.type !== "submit")
        .map((e) => ({ ...e, disabled: true }))
    : textForm;

  // ----- Tổng hợp điểm theo các mục A/B/C/D/E -----
  const breakdownRows = [
    { label: "A. Thành tích học tập", value: Number(values?.scoreB) || 0 },
    { label: "B. Thành tích khoa học công nghệ", value: autoScoreA },
    { label: "C. Khen thưởng thành tích KHCN", value: Number(values?.scoreD) || 0 },
    { label: "D. Hoạt động Đoàn, Hội", value: Number(values?.scoreE) || 0 },
    {
      label: "E. Ưu tiên",
      value: (Number(values?.scoreF) || 0) + (Number(values?.scoreG) || 0),
    },
  ];
  const score =
    Math.round(breakdownRows.reduce((s, r) => s + r.value, 0) * 100) / 100;

  if (loading) return null;

  return (
    <Paper style={{ padding: 12 }}>
      <Typography
        variant="h6"
        style={{ textAlign: "center", fontWeight: 700 }}
      >
        CHẤM ĐIỂM HỒ SƠ
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        style={{ padding: "8px 0" }}
      >
        <Typography variant="h2" style={{ color: "#ee6064" }}>
          <b>{score}</b>
        </Typography>
        <Typography variant="subtitle1">Tổng điểm của ứng viên</Typography>
      </Box>
      <Box width="100%" style={{ marginBottom: 12 }}>
        {breakdownRows.map((row) => (
          <Box
            key={row.label}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{ padding: "6px 0", borderBottom: "1px solid #eee" }}
          >
            <Typography variant="body2">{row.label}</Typography>
            <Typography variant="body2">
              <b>{Math.round((row.value || 0) * 100) / 100}</b>
            </Typography>
          </Box>
        ))}
        <Box
          display="flex"
          justifyContent="space-between"
          style={{ padding: "8px 0" }}
        >
          <Typography variant="subtitle1">
            <b>Tổng điểm (A+B+C+D+E)</b>
          </Typography>
          <Typography variant="subtitle1" style={{ color: "#ee6064" }}>
            <b>{score}</b>
          </Typography>
        </Box>
      </Box>
      <KPTextform
        textForm={formRows}
        handleSubmit={handleSubmit}
        values={values}
        setValues={setValues}
      />
      <Loading open={loading} />
    </Paper>
  );
}
